// First-touch acquisition cookie. Signup CTAs are plain links to
// app.mychildcareapp.com, so the landing URL's utm_*/click IDs never reach
// Rails. Instead, the first visit records them in a cookie on the shared
// parent domain, which the app reads when the account is created.
//
// Contract (the Rails side depends on this — change it only with a version
// bump):
//   name    mca_first_touch
//   domain  .mychildcareapp.com, path /, 90 days, Secure, SameSite=Lax
//   value   encodeURIComponent(<query string>), where the query string holds
//           v=1, ts (unix seconds), lp (landing path), ref (external
//           referrer, origin + path only), and whichever of utm_source,
//           utm_medium, utm_campaign, utm_term, utm_content, gclid, fbclid,
//           msclkid, oppref were on the landing URL. Absent keys are omitted.
// Rack already unescapes cookie values once, so in Rails
// `Rack::Utils.parse_query(cookies[:mca_first_touch])` yields the hash.
//
// First touch wins among touches that carry a source (a UTM, click ID or
// external referrer). A source-less one, such as a bare direct visit, is
// still recorded but gives way to the first sourced touch after it —
// otherwise a direct first visit would hide the ad that later earned the
// signup for the cookie's whole 90 days. This sits alongside, and does not
// replace, the cookies GA4 (_ga), Meta (_fbc) and OpenAI (__oppref) keep for
// their own attribution.

const COOKIE = "mca_first_touch"
const DOMAIN = "mychildcareapp.com"
const MAX_AGE = 90 * 24 * 60 * 60
const TEXT_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]
const CLICK_IDS = ["gclid", "fbclid", "msclkid", "oppref"]

const hasSource = (touch) => [...TEXT_PARAMS, ...CLICK_IDS, "ref"].some((key) => touch.has(key))

export function setupFirstTouch() {
  // Marketing host only: a Domain=.mychildcareapp.com cookie can't be set
  // from localhost or a Render preview anyway, and if the app ever loaded
  // this it would record its own sign-up page as the landing page.
  if (location.hostname !== DOMAIN) return

  const query = new URLSearchParams(location.search)
  const touch = new URLSearchParams({ v: "1", ts: String(Math.floor(Date.now() / 1000)), lp: location.pathname.slice(0, 200) })

  // Free text gets truncated; a truncated click ID is useless as a join key,
  // so an oversized one is dropped instead.
  TEXT_PARAMS.forEach((key) => {
    const value = query.get(key)?.trim()
    if (value) touch.set(key, value.slice(0, 200))
  })
  CLICK_IDS.forEach((key) => {
    const value = query.get(key)?.trim()
    if (value && value.length <= 500) touch.set(key, value)
  })

  // Referrer's query string is dropped (search terms, emails in links), and
  // our own hosts aren't an acquisition source.
  try {
    const ref = new URL(document.referrer)
    if (ref.hostname !== DOMAIN && !ref.hostname.endsWith(`.${DOMAIN}`)) {
      touch.set("ref", (ref.origin + ref.pathname).slice(0, 200))
    }
  } catch {}

  const existing = document.cookie.split("; ").find((c) => c.startsWith(`${COOKIE}=`))
  if (existing) {
    let prior
    try {
      prior = new URLSearchParams(decodeURIComponent(existing.slice(COOKIE.length + 1)))
    } catch {
      prior = new URLSearchParams()
    }
    if (hasSource(prior) || !hasSource(touch)) return
  }

  document.cookie = `${COOKIE}=${encodeURIComponent(touch.toString())}; Domain=.${DOMAIN}; Path=/; Max-Age=${MAX_AGE}; Secure; SameSite=Lax`
}
